import PropTypes from 'prop-types'

// material-ui
import { useTheme } from '@mui/material/styles'
import Avatar from '@mui/material/Avatar'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Divider from '@mui/material/Divider'

// project-import
import Chip from '../../../../ui-component/extended/Chip'

// assets
import { IconBrandTelegram, IconBuildingStore, IconMailbox, IconPhoto } from '@tabler/icons-react'
import User1 from '../../../../../assets/images/users/user-round.svg'
import { ReactNode } from 'react'

const ListItemWrapper = ({ children }: { children: ReactNode }) => {
  return (
    <Box
      sx={{
        p: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        cursor: 'pointer',
        '&:hover': {
          bgcolor: 'primary.light'
        }
      }}
    >
      {children}
    </Box>
  )
}

ListItemWrapper.propTypes = {
  children: PropTypes.node
}

// ==============================|| NOTIFICATION LIST ITEM ||============================== //

import { useGetNotificationsQuery, useMarkAsReadMutation } from '../../../../../app/services/notification'
import { useNavigate } from 'react-router-dom'
import { IconCash, IconAlertTriangle } from '@tabler/icons-react'
import moment from 'moment'

const NotificationList = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { data: notificationData } = useGetNotificationsQuery({ limit: 10, page: 1 })
  const [markAsRead] = useMarkAsReadMutation()
  const notifications = notificationData?.rows || []

  const handleNotificationClick = (notification: any) => {
    markAsRead(notification.id)
    if (notification.link) {
      navigate(notification.link)
    }
  }

  const chipSX = {
    height: 24,
    padding: '0 6px'
  }
  const chipErrorSX = {
    ...chipSX,
    color: theme.palette.error.dark,
    backgroundColor: theme.palette.error.light,
    marginRight: '5px'
  }

  const chipWarningSX = {
    ...chipSX,
    color: theme.palette.warning.dark,
    backgroundColor: theme.palette.warning.light
  }

  if (notifications.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant='subtitle2'>No notifications</Typography>
      </Box>
    )
  }

  return (
    <List
      sx={{
        width: '100%',
        maxWidth: 330,
        py: 0,
        borderRadius: '10px',
        [theme.breakpoints.down('md')]: {
          maxWidth: 300
        },
        '& .MuiListItemSecondaryAction-root': {
          top: 22
        },
        '& .MuiDivider-root': {
          my: 0
        },
        '& .list-container': {
          pl: 7
        }
      }}
    >
      {notifications.map((notification: any) => (
        <div key={notification.id}>
          <ListItemWrapper>
            <ListItem alignItems='center' onClick={() => handleNotificationClick(notification)}>
              <ListItemAvatar>
                <Avatar
                  sx={{
                    color: notification.type === 'ADVANCE_RECONCILE' ? theme.palette.warning.dark : theme.palette.primary.dark,
                    backgroundColor:
                      notification.type === 'ADVANCE_RECONCILE' ? theme.palette.warning.light : theme.palette.primary.light
                  }}
                >
                  {notification.type === 'ADVANCE_RECONCILE' ? <IconCash stroke={1.5} size='1.3rem' /> : <IconAlertTriangle stroke={1.5} size='1.3rem' />}
                </Avatar>
              </ListItemAvatar>
              <ListItemText primary={<Typography variant='subtitle1' sx={{ fontWeight: notification.isRead ? 400 : 700 }}>{notification.title}</Typography>} />
              <ListItemSecondaryAction>
                <Grid container justifyContent='flex-end'>
                  <Grid item xs={12}>
                    <Typography variant='caption' display='block' gutterBottom>
                      {moment(notification.createdAt).fromNow()}
                    </Typography>
                  </Grid>
                </Grid>
              </ListItemSecondaryAction>
            </ListItem>
            <Grid container direction='column' className='list-container' onClick={() => handleNotificationClick(notification)}>
              <Grid item xs={12} sx={{ pb: 1 }}>
                <Typography variant='subtitle2'>{notification.content}</Typography>
              </Grid>
              {!notification.isRead && (
                <Grid item xs={12}>
                  <Chip label='New' sx={chipWarningSX} />
                </Grid>
              )}
            </Grid>
          </ListItemWrapper>
          <Divider />
        </div>
      ))}
    </List>
  )
}

export default NotificationList
